import { ArrowPath, ArrowRightOnRectangle, ArrowUpRightOnBox } from "@medusajs/icons"
import { clx } from "@medusajs/ui"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAppUpdate } from "../lib/app-update"
import { useNewOrderSound } from "../lib/cha-ching"
import { useAuth } from "../lib/auth"
import { caps, t } from "../lib/i18n"
import { medusaAdminUrl } from "../lib/medusa"
import { MORE, NAV, NAV_GROUPS, sectionFor, type NavItem } from "../lib/nav"
import { useNavigationMemory } from "../lib/trail"
import { OpenOrdersDock } from "./open-orders"
import { MoreDrawer } from "./more-drawer"
import { useTabs } from "../lib/tabs"

/**
 * Phone: the page, and a bottom bar with the four sections used all day plus More (every section,
 * grouped, with search). Desktop: a sidebar with every section in its group.
 * One list (lib/nav.ts) feeds both, so a section added there appears everywhere.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const location = useLocation()
  useNavigationMemory()
  const updated = useAppUpdate()
  useNewOrderSound()
  const current = sectionFor(location.pathname)
  // The person's own tabs between Today and More (lib/tabs.ts); More opens a sheet over the page.
  const tabs = useTabs()
  const barItems = [NAV[0], ...tabs.items]
  const [moreOpen, setMoreOpen] = useState(false)
  useEffect(() => setMoreOpen(false), [location.key])
  // Six tabs on a 375px phone: smaller, tighter labels so «Παραγγελίες» and «Περισσότερα» fit whole.
  const dense = barItems.length + 1 >= 6
  const onMore = moreOpen || location.pathname === MORE.to || (!!current && !barItems.some((item) => item.to === current.to))

  // Each tab remembers the view it was left on (Orders on "To arrange"), so moving between tabs
  // does not reset the list you were working through.
  const lastView = useRef(new Map<string, string>())
  useEffect(() => {
    if (current && location.pathname === current.to) lastView.current.set(current.to, location.search)
  }, [current, location.pathname, location.search])
  const hrefFor = (item: { to: string }) => `${item.to}${lastView.current.get(item.to) ?? ""}`

  return (
    <div className="flex min-h-full">
      <aside className="bg-ui-bg-base border-ui-border-base sticky top-0 hidden h-screen w-[248px] shrink-0 flex-col border-r lg:flex">
        <Link to="/" className="flex items-center gap-x-2.5 px-5 pb-2 pt-5">
          <img src="/logo.png" alt="High Club" className="h-6 w-auto" />
          <span className="hc-ref">HQ</span>
        </Link>
        <nav className="flex-1 overflow-y-auto px-3 pb-4 [scrollbar-width:thin]" aria-label={t("Sections")}>
          {NAV_GROUPS.map((group) => (
            <div key={group.key} className="mt-4 first:mt-2">
              <p className="hc-eyebrow px-2 pb-1.5">{caps(t(group.label))}</p>
              <ul className="flex flex-col gap-y-px">
                {NAV.filter((item) => item.group === group.key).map((item) => (
                  <li key={item.to}>
                    <SideLink item={item} to={hrefFor(item)} active={current?.to === item.to} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-ui-border-base flex flex-col gap-y-1 border-t p-3">
          <a
            href={medusaAdminUrl()}
            target="_blank"
            rel="noreferrer"
            className="txt-compact-small text-ui-fg-subtle hover:bg-ui-bg-base-hover hover:text-ui-fg-base flex items-center gap-x-2 rounded-md px-2 py-1.5"
          >
            <ArrowUpRightOnBox className="text-ui-fg-muted" /> {t("Medusa admin")}
          </a>
          <button
            type="button"
            onClick={() => void signOut()}
            className="txt-compact-small text-ui-fg-subtle hover:bg-ui-bg-base-hover hover:text-ui-fg-base flex items-center gap-x-2 rounded-md px-2 py-1.5 text-left"
          >
            <ArrowRightOnRectangle className="text-ui-fg-muted shrink-0" />
            <span className="min-w-0 flex-1 truncate">{t("Sign out")}</span>
            <span className="text-ui-fg-muted truncate text-[11px]">{user?.email}</span>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {updated && (
          <div className="bg-ui-fg-base text-ui-bg-base sticky top-0 z-40 flex items-center justify-between gap-x-3 px-4 py-2 lg:px-8" role="status">
            <span className="txt-compact-small-plus">{t("HQ has been updated.")}</span>
            <button type="button" onClick={() => window.location.reload()} className="txt-compact-small-plus bg-ui-bg-base text-ui-fg-base inline-flex h-8 shrink-0 items-center gap-x-1.5 rounded-full px-3">
              <ArrowPath /> {t("Load the new version")}
            </button>
          </div>
        )}
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-28 pt-4 lg:px-8 lg:pb-12 lg:pt-8">{children}</main>
        <OpenOrdersDock />
      </div>

      <nav
        className="hc-bar-paper border-ui-border-base pb-safe fixed inset-x-0 bottom-0 z-30 grid border-t lg:hidden"
        style={{ gridTemplateColumns: `repeat(${barItems.length + 1}, minmax(0, 1fr))` }}
        aria-label={t("Sections")}
      >
        {barItems.map((item) => (
          <TabLink key={item.to} to={hrefFor(item)} icon={item.icon} label={t(item.short ?? item.label)} dense={dense} active={!moreOpen && current?.to === item.to && location.pathname !== MORE.to} />
        ))}
        <MoreTab label={t(MORE.label)} dense={dense} active={onMore} onClick={() => setMoreOpen(true)} />
      </nav>
      <MoreDrawer open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  )
}

function SideLink({ item, to, active }: { item: NavItem; to: string; active: boolean }) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={clx(
        "txt-compact-small-plus relative flex h-8 items-center gap-x-2.5 rounded-md px-2 transition-colors",
        active ? "bg-ui-bg-subtle text-ui-fg-base" : "text-ui-fg-subtle hover:bg-ui-bg-base-hover hover:text-ui-fg-base"
      )}
    >
      {active && <span aria-hidden className="bg-ui-fg-interactive absolute -left-3 top-1.5 h-5 w-[3px] rounded-r-full" />}
      <item.icon className={active ? "text-ui-fg-base" : "text-ui-fg-muted"} />
      {t(item.label)}
    </Link>
  )
}

/** More, the last tab: it opens the side sheet rather than a page. */
const labelClass = (dense?: boolean) => clx("max-w-full truncate", dense ? "text-[10px] tracking-[-0.02em]" : "px-0.5")

function MoreTab({ label, active, onClick, dense }: { label: string; active: boolean; onClick: () => void; dense?: boolean }) {
  const Icon = MORE.icon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      className={clx("relative flex flex-col items-center gap-y-1 pb-1.5 pt-2.5 text-[11px] font-medium leading-none", active ? "text-ui-fg-base" : "text-ui-fg-muted")}
    >
      <span aria-hidden className={clx("absolute top-0 h-[3px] w-7 rounded-b-full transition-colors", active ? "bg-ui-fg-interactive" : "bg-transparent")} />
      <Icon className="size-[22px]" />
      <span className={labelClass(dense)}>{label}</span>
    </button>
  )
}

function TabLink({ to, icon: Icon, label, active, dense }: { to: string; icon: NavItem["icon"]; label: string; active: boolean; dense?: boolean }) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        // The tab you are already on takes you to the top of its page instead.
        if (active && window.location.pathname === to.split("?")[0]) {
          event.preventDefault()
          window.scrollTo({ top: 0, behavior: "smooth" })
        }
      }}
      className={clx("relative flex flex-col items-center gap-y-1 pb-1.5 pt-2.5 text-[11px] font-medium leading-none", active ? "text-ui-fg-base" : "text-ui-fg-muted")}
    >
      <span aria-hidden className={clx("absolute top-0 h-[3px] w-7 rounded-b-full transition-colors", active ? "bg-ui-fg-interactive" : "bg-transparent")} />
      <Icon className="size-[22px]" />
      <span className={labelClass(dense)}>{label}</span>
    </Link>
  )
}
